import React, { useState, useEffect } from 'react';
import { FaTimes, FaSave } from 'react-icons/fa';
import { InlineSpinner } from './LoadingState';
import Modal from './Modal';
import { getAgents } from '../api/api';
import DateTimeInput from './DateTimeInput';

// Convert a stored ISO-like ConversationTime string to datetime-local input value
function toDatetimeLocal(value) {
  if (!value) return '';
  try {
    // Handles "2025-01-15 09:30:00" or "2025-01-15T09:30:00" formats
    const normalised = value.replace(' ', 'T').substring(0, 16);
    // Validate it's a reasonable date string
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(normalised)) return '';
    return normalised;
  } catch {
    return '';
  }
}

// Convert datetime-local value back to ISO string stored in analytics
function fromDatetimeLocal(value) {
  if (!value) return '';
  // "2025-01-15T09:30" → "2025-01-15 09:30:00"
  return value.replace('T', ' ') + ':00';
}

const EditMetadataModal = ({ open, callData, onSave, onClose }) => {
  const [agent, setAgent]                   = useState('');
  const [datetime, setDatetime]             = useState('');
  const [conversationId, setConversationId] = useState('');
  const [dnis, setDnis]                     = useState('');
  const [saving, setSaving]                 = useState(false);
  const [error, setError]                   = useState('');
  const [agentOptions, setAgentOptions]     = useState([]);

  useEffect(() => {
    getAgents()
      .then(res => setAgentOptions(Array.isArray(res) ? res : []))
      .catch(() => {});
  }, []);

  // Reset fields whenever the modal opens with new data
  useEffect(() => {
    if (open && callData) {
      setAgent(callData.Agent || '');
      setDatetime(toDatetimeLocal(callData.ConversationTime));
      setConversationId(callData.Telephony?.Genesys?.conversationId || '');
      setDnis(callData.Telephony?.Genesys?.dnis || '');
      setError('');
    }
  }, [open, callData]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const metadata = {};
      if (agent.trim())          metadata.Agent            = agent.trim();
      if (datetime.trim())       metadata.ConversationTime = fromDatetimeLocal(datetime.trim());
      if (conversationId.trim()) metadata.ConversationId   = conversationId.trim();
      if (dnis.trim())           metadata.DNIS             = dnis.trim();

      await onSave(metadata);
    } catch (err) {
      setError(err?.message || 'Failed to save metadata. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Edit Call Metadata">
      <div className="space-y-4">
        <p className="text-sm text-gray-500">
          Update call metadata fields. Changes are saved to the call record immediately.
        </p>

        <div className="grid grid-cols-1 gap-4">
          {/* Agent */}
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1.5">
              Agent Name / ID
            </label>
            <input
              type="text"
              list="edit-metadata-agents"
              value={agent}
              onChange={e => setAgent(e.target.value)}
              placeholder="e.g. BobS"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
            />
            <datalist id="edit-metadata-agents">
              {agentOptions.map(a => <option key={a} value={a} />)}
            </datalist>
          </div>

          {/* Call Date & Time */}
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1.5">
              Call Date & Time
            </label>
            <DateTimeInput
              mode="datetime"
              value={datetime}
              onChange={e => setDatetime(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
            />
          </div>

          {/* Conversation ID */}
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1.5">
              Conversation ID
            </label>
            <input
              type="text"
              value={conversationId}
              onChange={e => setConversationId(e.target.value)}
              placeholder="e.g. fd4bd0f6-52c2-4fab-97de-8f7518474403"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
            />
          </div>

          {/* DNIS */}
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1.5">
              DNIS
            </label>
            <input
              type="text"
              value={dnis}
              onChange={e => setDnis(e.target.value)}
              placeholder="e.g. +18005551234"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
            />
          </div>
        </div>

        {error && (
          <p className="text-sm text-red-600">{error}</p>
        )}

        <div className="flex flex-wrap justify-end gap-3 pt-2">
          <button
            onClick={onClose}
            disabled={saving}
            className="btn-outline disabled:opacity-50"
          >
            <FaTimes />
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary disabled:opacity-50"
          >
            {saving ? (
              <InlineSpinner light className="mr-2" />
            ) : (
              <FaSave className="mr-2" />
            )}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default EditMetadataModal;
